// src\common\components\widgets\WidgetResponseDetails.tsx
import { WidgetProps } from '../../models/widget.model';
import DataGrid from '../datagrid/DataGrid';

import CardsGrid, { GridCardType } from '../cards-grid/CardsGrid';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectViewStateForWidget } from '../../store/slices/view-toggle.slice';
import { TagRepositoryRegisterPayload } from '../../models/tag.model';
import { addTagRepository, selectTagEditorVisible } from '../../store/slices/tag-editor.slice';
import { ListResponse } from '../../store/api/chart.api';
import { formatAppDate } from '../../hooks/utils';
import { TagCell } from '../datagrid/TagCell';
import { DateCell } from '../datagrid/DateCell';
import { TagEditor } from '../tags/TagEditor';
import { AttachmentColumn } from 'common/components/widgets/widget-subcomponents/Attachment';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature } from 'common/models/page-config.models';
import { useWidgetSearchFilter } from './widget-hooks/useWidgetSearchFilter';
import TeamMemberCell from '../datagrid/TeamMemberCell';
import ProfileNameCell from '../datagrid/ProfileNameCell';
import TextCell from '../datagrid/TextCell';
import { ColumnModel } from 'common/models/widget-config.model';

export const columns: ColumnModel[] = [
  {
    title: 'Date',
    field: 'date',
    filter: 'date',
    sortable: true,
    width: '100px',
    groupable: true,
    cell: DateCell,
  },
  {
    title: 'Team Member',
    field: 'fieldTeam',
    width: '180px',
    sortable: true,
    groupable: true,
    cell: TeamMemberCell,
  },
  {
    title: 'Profile',
    field: 'profile',
    width: '180px',
    sortable: true,
    groupable: true,
    cell: ProfileNameCell,
  },
  {
    sortable: true,
    groupable: true,
    title: 'Specialty',
    field: 'specialtyName',
    width: '130px',
  },
  {
    sortable: true,
    groupable: true,
    title: 'Response',
    field: 'response',
    width: '220px',
    cell: TextCell,
  },
  {
    sortable: true,
    groupable: false,
    title: 'Response Details',
    field: 'responseDetail',
    width: '400px',
    cell: TextCell,
  },
  {
    sortable: false,
    title: 'Tags',
    field: 'tags',
    width: '400px',
    groupable: false,
    cell: TagCell,
  },
  {
    groupable: true,
    title: 'Category',
    field: 'survey',
    width: '220px',
  },
  {
    groupable: true,
    title: 'Question',
    field: 'question',
    width: '220px',
  },
  {
    sortable: true,
    title: 'Insight Source',
    field: 'insightSourceName',
    width: '220px',
    groupable: false,
    cell: TextCell,
  },
];

const card: GridCardType = {
  topRowParams: [
    {
      name: 'date',
      sequence: 1,
      callback: (date: any) => formatAppDate(new Date(date), 'MM/dd/yyyy'),
    },
    {
      name: 'profile',
      sequence: 2,
    },
    {
      name: 'specialtyName',
      sequence: 3,
    },
    {
      name: 'fieldTeam',
      sequence: 4,
    },
  ],
  descriptionTitle: 'response',
  descriptionBody: 'responseDetail',
  tags: 'tags',
};

const VIEW = {
  GRID: 0,
  CARD_LIST: 1,
};

const SELECTION_TYPE = 'response';

export default function WidgetResponseDetails(props: Readonly<WidgetProps<ListResponse>>) {
  const dispatch = useAppDispatch();
  const widgetViews = useAppSelector(selectViewStateForWidget);
  const viewIndex = widgetViews[props.id] ? widgetViews[props.id] : 0;
  const { widgetConfig } = props;
  const { hasFeature } = useFeatureFlags();
  const hasFileUpload = hasFeature([Feature.CaptureFileUpload, Feature.CaptureOrganicFileUpload]);
  const tagEditorVisible = useAppSelector(selectTagEditorVisible);
  const searchColumns = [
    'date',
    'profile',
    'responseDetail',
    'response',
    'profile',
    'persona',
    'fieldTeam',
    'specialtyName',
    'tags',
  ];
  const { dataStateChange, dataResult, dataState, totalCount } = useWidgetSearchFilter({
    searchColumns,
    widget: props.widget,
    data: props.data,
  });

  useEffect(() => {
    const payload: TagRepositoryRegisterPayload = {
      repository: SELECTION_TYPE,
      records: {},
    };
    dataResult.data.forEach((row: { responseId: number; tags: any[] }) => {
      payload.records[row.responseId] = row.tags;
    });
    dispatch(addTagRepository(payload));
  }, [dispatch, dataResult]);
  const gridColumns = (hasFileUpload ? [...columns, AttachmentColumn] : columns).filter(
    (col) => !widgetConfig?.hiddenColumns.includes(col.field)
  );
  if (viewIndex === VIEW.GRID) {
    return (
      <>
        {tagEditorVisible && <TagEditor />}
        <DataGrid
          widgetId={props.id}
          onDataStateChange={dataStateChange}
          {...dataState}
          groupable={props.groupable}
          total={totalCount}
          sortable={true}
          data={dataResult}
          columns={gridColumns}
          resizable={true}
        />
      </>
    );
  }
  return (
    <div className="widget-response-details">
      {tagEditorVisible && <TagEditor />}
      <div className="k-scrollable">
        <CardsGrid total={totalCount} widgetId={props.id} data={dataResult} card={card} />
      </div>
    </div>
  );
}
